import { useEffect, useRef, useState } from 'react'
import { api } from '../api'
import { navigate } from '../router'
import type { Invoice, InvoiceSnapshot } from '../../shared/types'
import InvoiceDocument from './InvoiceDocument'
import { pushError, pushToast } from '../toast'

// Browsers seed the "Save as PDF" filename from document.title, so we swap the
// title for the duration of the print and restore it afterwards. Characters the
// OS forbids in filenames are replaced rather than dropped.
function sanitizeFilePart(s: string): string {
  return s.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim()
}

// Reopen a saved invoice, rendered entirely from its frozen snapshot. The Print
// button uses window.print(); the print stylesheet hides all chrome and prints
// the .invoice-sheet alone on US Letter.
export default function InvoiceView({ invoiceId }: { invoiceId: number }) {
  const [invoice, setInvoice] = useState<Invoice | null>(null)
  const [snap, setSnap] = useState<InvoiceSnapshot | null>(null)
  const [clientName, setClientName] = useState<string | null>(null)

  useEffect(() => {
    ;(async () => {
      try {
        const inv = await api.getInvoice(invoiceId)
        setInvoice(inv)
        setSnap(JSON.parse(inv.snapshot_json) as InvoiceSnapshot)
        // Short name for the filename only — the document itself still renders
        // the legal name frozen in the snapshot.
        api
          .getClient(inv.client_id)
          .then((r) => setClientName(r.client.simple_name))
          .catch(() => {})
      } catch (e) {
        pushError(`Couldn't load invoice: ${(e as Error).message}`)
      }
    })()
  }, [invoiceId])

  // Restore the page title if the component unmounts mid-print.
  const prevTitle = useRef<string | null>(null)
  useEffect(
    () => () => {
      if (prevTitle.current !== null) document.title = prevTitle.current
    },
    [],
  )

  function printInvoice() {
    if (!snap) return
    const parts = [snap.sender.name, clientName ?? snap.client.legal_name, snap.invoice_number]
      .map((p) => (p ? sanitizeFilePart(p) : ''))
      .filter(Boolean)
    if (parts.length) {
      prevTitle.current = document.title
      document.title = parts.join(' - ')
      const restore = () => {
        if (prevTitle.current !== null) document.title = prevTitle.current
        prevTitle.current = null
        window.removeEventListener('afterprint', restore)
      }
      window.addEventListener('afterprint', restore)
    }
    window.print()
  }

  if (!invoice || !snap) return <div className="p-10 text-slate-400">Loading…</div>

  async function release() {
    if (!invoice) return
    const ok = confirm(
      `Release invoice ${invoice.invoice_number}?\n\nIt will be deleted and its time entries will become editable again so you can make corrections. If it's the most recent invoice for this client, its number is freed for reuse.\n\nThis cannot be undone.`,
    )
    if (!ok) return
    try {
      const res = await api.releaseInvoice(invoice.id)
      pushToast(res.number_reused ? 'Invoice released — number freed for reuse' : 'Invoice released')
      navigate(`#/client/${invoice.client_id}/invoices`)
    } catch (e) {
      pushError(`Couldn't release invoice: ${(e as Error).message}`)
    }
  }

  async function togglePaid() {
    if (!invoice) return
    const next = invoice.status === 'paid' ? 'issued' : 'paid'
    try {
      const updated = await api.setInvoiceStatus(invoice.id, next)
      setInvoice(updated)
    } catch (e) {
      pushError(`Couldn't update status: ${(e as Error).message}`)
    }
  }

  return (
    <div className="min-h-full bg-slate-100 pb-16">
      <div className="no-print sticky top-0 z-10 flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-white px-4 py-3 sm:px-6">
        <button
          className="text-sm text-slate-500 hover:text-slate-800"
          onClick={() => navigate(`#/client/${invoice.client_id}/invoices`)}
        >
          ← Invoices
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={release}
            className="rounded-md px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50"
            title="Delete this invoice and make its entries editable again"
          >
            Release / edit
          </button>
          <button
            onClick={togglePaid}
            className={
              'rounded-full px-3 py-1 text-xs font-medium ' +
              (invoice.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')
            }
          >
            {invoice.status === 'paid' ? '✓ Paid' : 'Mark paid'}
          </button>
          <button
            className="rounded-md bg-slate-900 px-4 py-1.5 font-medium text-white hover:bg-slate-700"
            onClick={printInvoice}
          >
            Print / Save PDF
          </button>
        </div>
      </div>

      <div className="px-2 py-5 sm:px-6 sm:py-8">
        <InvoiceDocument snap={snap} />
      </div>
    </div>
  )
}
